import ormaApi from './OrmaApi';

/**
 * Fetch all groups or filter with optional query
 * @param {Object} query - Optional MongoDB query object (e.g., { group_id: { $in: ['id1', 'id2'] } })
 * @returns {Promise<{groups: Array, count: number}>} Groups data with count
 */
export const fetchGroups = async (query) => {
  try {
    const response = query
      ? await ormaApi.post('/api/groups', { query })
      : await ormaApi.get('/api/groups');
    
    // Access nested data structure from ResponseHandler
    const data = response.data?.data || response.data;
    return {
      groups: data.groups || [],
      count: data.count || 0,
    };
  } catch (error) {
    console.error('Error fetching groups:', error);
    throw error;
  }
};

/**
 * Create a new group
 * @param {Object} groupData - Group data including required group_id
 * @returns {Promise<Object|null>} Created group object or null if failed
 */
export const createGroup = async (groupData) => {
  try {
    const response = await ormaApi.post('/api/groups/create', groupData);
    // Access nested data structure from ResponseHandler
    const data = response.data?.data || response.data;
    return data.group || null;
  } catch (error) {
    console.error('Error creating group:', error);
    if (error.response?.status === 409) {
      // Group already exists - return null to indicate conflict
      return null;
    }
    throw error;
  }
};

/**
 * Find a group by group_id
 * @param {string} groupId - The group_id to search for
 * @returns {Promise<Object|null>} Group object or null if not found
 */
export const getGroupById = async (groupId) => {
  try {
    // Note: This requires a corresponding backend endpoint: POST /api/groups/find
    const response = await ormaApi.post(`/api/groups/find`, { groupId });
    // Access nested data structure from ResponseHandler
    const data = response.data?.data || response.data;
    return data.group || null;
  } catch (error) {
    console.error(`Error fetching group ${groupId}:`, error);
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Update a group by group_id
 * @param {string} groupId - The group_id to update
 * @param {Object} updates - Partial group object with fields to update
 * @returns {Promise<Object|null>} Updated group object or null if not found
 */
export const updateGroupById = async (groupId, updates) => {
  try {
    const payload = { ...updates, groupId };
    const response = await ormaApi.patch(`/api/groups/update`, payload);
    // Access nested data structure from ResponseHandler
    const data = response.data?.data || response.data;
    return data.group || null;
  } catch (error) {
    console.error(`Error updating group ${groupId}:`, error);
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};
